// TODO refactor all of this, tons of repeated code especially between
// individual and joint
import validator from 'ui-validator';
import { replaceAllString } from '../../shared_modules/baui-utils';
import pi1ValidatorSchema from '../pi1/pi1-schema-validators';
import { skipIfMissing, isTruthy, homeStatusRequired } from '../../shared_modules/baui-utils/client/validation';
import { SENTILINK_AGREEMENTS } from '../../shared_modules/local-propulsion/components/SentilinkAgreement/constants';
import {
  INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
  EMPLOYEMENT_TYPES,
  CUSTOM_REGULAR_EXP,
} from '../../shared_modules/baui-utils/client/constants';

const v = validator.validators;

/**
 * Returns error if not valid social security number format
 * @param  {STRING} options.message  - custom message for error
 */
const lastFourSSN = validator.createValidator((options, value, key, attrs) => {
  const opts = options || {};
  const condition = new RegExp('^\\d{4}$');
  if (value && !condition.test(value)) {
    return opts.message || 'Social Security Number entered is not valid';
  }
});

// TODO: Move this to utils in shared modules.
const getRawNumber = val => val && val.match(/(\d+)/g)?.join('');

const isSameAsPrimaryPhone = validator.createValidator((options, value, key, phoneNumberToCompare) => {
  if (phoneNumberToCompare !== '' && value !== undefined && value === phoneNumberToCompare) {
    const keyStringVal = key === 'workPhone' || key === 'workPhoneSecondary' ? 'work phone' : 'alternate phone';
    return 'Primary phone and ' + keyStringVal + ' cannot be the same';
  }
});

export const isPrimaryPhoneSameAsWorkPhone = validator.createValidator((options, value, key, numberToCompare) => {
  const isCompare =
    numberToCompare !== '' && value !== undefined && getRawNumber(numberToCompare) === getRawNumber(value);
  if (isCompare) {
    let message;
    if (key === 'primaryPhoneSecondary') message = 'Personal phone cannot be same as work phone';
    if (key === 'workPhone' || key === 'workPhoneSecondary') message = 'Work phone cannot be same as personal phone';
    return message;
  }
});

const getEmploymentStatus = (attrs, isSecondary) =>
  attrs?.[`employmentStatus${isSecondary ? 'Secondary' : ''}`]?.toLowerCase();

export const checkEmploymentStatus = (attrs, statuses, isSecondary) =>
  statuses.includes(attrs?.[`employmentStatus${isSecondary ? 'Secondary' : ''}`]?.toLowerCase() ?? '');

const tenureRange = (value, key, attrs, isSecondary) => {
  // TODO: PI2 is validating dateOfBirth key on client side but on Server side they are not validating anything.
  // For Repeats, we are using same validations on client/server, so we added a flag to keep other flows untouched.
  const attrsDate = attrs.verifyDobSecondary && isSecondary ? attrs.dobSecondary : attrs.dateOfBirth;
  const date = new Date(attrsDate);
  let age = new Date().getFullYear() - date.getFullYear();
  if (attrs.tenureInMonths) age *= 12;
  return v.range({ min: 0, max: (age - 1).toString(), message: 'Number of years must be less than your age' })(
    value,
    key,
    attrs
  );
};

const allValidations = {
  selfReportedMarketingAttribution: true,
  primaryPhone: {
    // required: skipIfMissing({ validateFn: v.required({ key: 'primary phone number' }) }),
    // validTelephone: v.validTelephone(),
    required: skipIfMissing({ validateFn: v.required({ message: 'Enter a valid phone number' }) }),
    validTelephone: v.validTelephone({ message: 'Enter a valid phone number' }),
    suspicious: v.suspicious(),
  },
  primaryPhoneSource: {},
  password: {
    required: skipIfMissing({ validateFn: v.required({ message: 'A password is required' }) }),
    validPassword: skipIfMissing({ validateFn: v.validPassword() }),
    inputLength: skipIfMissing({ validateFn: v.inputLength({ min: 8, max: 40, key: 'Your password' }) }),
    // TODO notSameAsField: skipIfMissing({ validateFn: v.notSameAsField({ key: 'Your password', target: 'email', message: 'Your password must not match your login email' }) }),
    suspicious: v.suspicious(),
  },
  smsMarketingOptIn: {
    suspicious: v.suspicious(),
  },
  ssn: {
    required: (value, key, attrs) => {
      if (attrs.hasOwnProperty(key)) {
        return v.required({ message: 'Your Social Security number is required' })(value, key, attrs);
      }
    },
    validSSN: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key)) {
        return v.validSSN()(replaceAllString(value), key, attributes);
      }
    },
    suspicious: v.suspicious(),
  },
  last4ssn: {
    required: skipIfMissing({ validateFn: v.required({ message: 'Your Social Security number is required' }) }),
    suspicious: v.suspicious(),
    lastFourSSN: skipIfMissing({ validateFn: lastFourSSN() }),
  },
  zip: {
    required: skipIfMissing({ validateFn: v.required({ key: 'zip code' }) }),
    validZipcode: skipIfMissing({ validateFn: v.validZipcode() }),
    suspicious: v.suspicious(),
  },
  employmentStatus: {
    required: v.required({ message: 'Select one option to continue' }),
    suspicious: v.suspicious(),
  },
  incomeSource: {
    suspicious: v.suspicious(),
  },
  employer: {
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'selfemployed', 'part_time', 'contractor'])) {
        return v.required({
          message:
            getEmploymentStatus(attrs) === 'selfemployed'
              ? 'Enter a valid business name'
              : 'Enter a valid employer name',
        })(value, key, attrs);
      }
    },
    suspicious: v.suspicious(),
    inputLength: (value, key, attrs) => {
      const statuses = ['employed', 'selfemployed', 'contractor', 'part_time'];
      if (attrs[key] && attrs[key].trim().length > 0 && checkEmploymentStatus(attrs, statuses)) {
        return v.inputLength({
          max: 40,
          message: `Your ${
            getEmploymentStatus(attrs) === 'selfemployed' ? 'business' : 'employer'
          } name must be 40 characters or less.`,
        })(value, key, attrs);
      }
    },
    customCondition: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
        return v.customCondition({
          condition: CUSTOM_REGULAR_EXP,
          message: INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
        })(value, key, attrs);
      }
    },
  },
  // LMS-116884 - This key is blocking PI2 Submit, we are not sending businessName anymore,
  // is handling by employer line 96.
  // businessName: {
  //   // TODO: does pi2 locked still send this? if so, get it to send employer like pi2-1qaat
  //   required: (value, key, attrs) => {
  //     if (checkEmploymentStatus(attrs, ['selfemployed'])) {
  //       return v.required({ message: 'Please enter the name of your business' })(value, key, attrs);
  //     }
  //   },
  //   suspicious: v.suspicious(),
  //   inputLength: (value, key, attrs) => {
  //     if (attrs[key] && attrs[key].trim().length > 0 && checkEmploymentStatus(attrs, ['selfemployed'])) {
  //       return v.inputLength({
  //         max: 40,
  //         message: 'Your business name must be 40 characters or less.',
  //       })(value, key, attrs);
  //     }
  //   },
  // },
  income: pi1ValidatorSchema.income,
  position: {
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed'])) {
        return v.required({ message: 'Enter a valid job title' })(value, key, attrs);
      }
    }, // edited the below to change position to job title to match the 'required' messaging from pi2
    customCondition: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
        return v.customCondition({
          condition: CUSTOM_REGULAR_EXP,
          message: INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
        })(value, key, attrs);
      }
    },
    suspicious: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
        return v.suspicious()(value, key, attrs);
      }
    },
    inputLength: (value, key, attrs) => {
      if (attrs[key] && attrs[key].trim().length > 0 && checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
        return v.inputLength({
          max: 40,
          message: 'Your job title must be 40 characters or less.',
        })(value, key, attrs);
      }
    },
  },
  tenure: {
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'part_time'])) {
        return v.required({ message: 'Enter a valid number of years' })(value, key, attrs);
      }
    },
    range: (value, key, attrs) => {
      return tenureRange(value, key, attrs);
    },
    suspicious: v.suspicious(),
    isNumber: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
          return v.isNumber({ message: 'Please select a valid job tenure' })(value, key, attrs);
        }
      },
    }),
  },
  workAddress: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (attrs.skipWorkAddress) {
          return;
        }
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'])) {
          return v.required({ message: 'Your work address is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
  },
  city: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'])) {
          return v.required({ message: 'A valid city is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
    customCondition: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
        return v.customCondition({
          condition: CUSTOM_REGULAR_EXP,
          message: INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
        })(value, key, attrs);
      }
    },
  },
  state: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'])) {
          return v.required({ message: 'A valid state is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
  },
  zipCode: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'])) {
          return v.required({ message: 'A valid zip code is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
    validZipcode: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES)) {
        return v.validZipcode()(value, key, attrs);
      }
    },
  },
  workPhone: {
    // this seems only required for employed, parttime and optional for others
    // (using pi2-1qaat/contact-validations)
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'part_time'])) {
        return v.required({ message: 'Enter a valid phone number' })(value, key, attrs);
      }
    },
    suspicious: v.suspicious(),
    validTelephone: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, EMPLOYEMENT_TYPES))
        return v.validTelephone({ message: 'Enter a valid phone number' })(value, key, attrs);
    },
    // TODO: This validation should be removed once PBS allows same WorkPhone and PrimaryPhone.
    isPrimaryPhoneSameAsWorkPhone: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (checkEmploymentStatus(attrs, statuses, false)) {
        const compareNumber = attrs.loanType === 'joint' ? attrs.primaryPhoneSecondary : attrs.primaryPhone;
        return isPrimaryPhoneSameAsWorkPhone()(value, key, compareNumber);
      }
    },
  },
  alternatePhone: {
    suspicious: v.suspicious(),
    validTelephone: v.validTelephone(),
    isSameAsPrimaryPhone: (value, key, attrs) => {
      const phoneNumberToCompare = attrs.primaryPhone;
      return isSameAsPrimaryPhone()(value, key, phoneNumberToCompare);
    },
  },
  homeStatus: {
    required: (value, key, attrs) => {
      return homeStatusRequired(value, key, attrs);
    },
  },
  homeStatusSecondary: {
    required: (value, key, attrs) => {
      return homeStatusRequired(value, key, attrs);
    },
  },
  rentPaymentAmt: {},
  mortgagePaymentAmt: {},
  primaryPhoneSecondary: {
    required: (value, key, attrs) => {
      // debugger;
      if (attrs.hasOwnProperty(key)) {
        // debugger; compare to skipIfMissing etc. should be the same? TODO
        return v.required({ key: 'primary phone number' })(value, key, attrs);
      }
    },
    validTelephone: v.validTelephone(),
    suspicious: v.suspicious(),
  },
  primaryPhoneSourceSecondary: {},
  ssnSecondary: {
    required: (value, key, attrs) => {
      if (attrs.hasOwnProperty(key)) {
        return v.required({ message: 'The Social Security number is required' })(value, key, attrs);
      }
    },
    validSSN: (value, key, attributes) => {
      if (Object.prototype.hasOwnProperty.call(attributes, key)) {
        return v.validSSN()(replaceAllString(value), key, attributes);
      }
    },
    suspicious: v.suspicious(),
    isNotSameAsPrimarySSN: (value, key, attrs) => {
      if (attrs.hasOwnProperty(key)) {
        return v.notSameAsField({
          target: 'ssn',
          message: 'Social Security Numbers for 1st and 2nd Co-Borrowers cannot be the same',
        })(value, key, attrs);
      }
    },
  },
  rentPaymentAmtSecondary: {},
  mortgagePaymentAmtSecondary: {},
  employmentStatusSecondary: {
    required: (value, key, attrs) => {
      if (attrs.hasOwnProperty(key)) {
        return v.required({ message: 'Select one of the following options to continue' })(value, key, attrs);
      }
    },
    suspicious: v.suspicious(),
  },
  incomeSourceSecondary: {
    suspicious: v.suspicious(),
  },
  employerSecondary: {
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'selfemployed', 'part_time', 'contractor'], true)) {
        return v.required({
          message:
            getEmploymentStatus(attrs, true) === 'selfemployed'
              ? 'Enter a valid business name'
              : 'Enter the name of your co-borrower’s employer',
        })(value, key, attrs);
      }
    },
    suspicious: v.suspicious(),
    inputLength: (value, key, attrs) => {
      const statuses = ['employed', 'selfemployed', 'contractor', 'part_time'];
      if (attrs[key] && attrs[key].trim().length > 0 && checkEmploymentStatus(attrs, statuses, true)) {
        return v.inputLength({
          max: 40,
          message: `Your ${
            getEmploymentStatus(attrs, true) === 'selfemployed' ? 'business' : 'employer'
          } name must be 40 characters or less.`,
        })(value, key, attrs);
      }
    },
    customCondition: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time'];
      if (checkEmploymentStatus(attrs, statuses, true)) {
        return v.customCondition({
          condition: CUSTOM_REGULAR_EXP,
          message: INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
        })(value, key, attrs);
      }
    },
  },
  // LMS-116884 - This key is blocking PI2 Submit, we are not sending businessNameSecondary anymore,
  // is handling by employerSecondary line 336.
  // businessNameSecondary: {
  //   required: (value, key, attrs) => {
  //     if (checkEmploymentStatus(attrs, ['selfemployed'], true)) {
  //       return v.required({ message: 'Please enter the name of your business' })(value, key, attrs);
  //     }
  //   },
  //   suspicious: v.suspicious(),
  //   inputLength: (value, key, attrs) => {
  //     if (attrs[key] && attrs[key].trim().length > 0 && checkEmploymentStatus(attrs, ['selfemployed'], true)) {
  //       return v.inputLength({
  //         max: 40,
  //         message: 'Your business name must be 40 characters or less.',
  //       })(value, key, attrs);
  //     }
  //   },
  // },
  positionSecondary: {
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed'], true)) {
        return v.required({ message: 'Please enter a valid job title' })(value, key, attrs);
      }
    },
    customCondition: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (checkEmploymentStatus(attrs, statuses, true)) {
        return v.customCondition({
          condition: '.*(d[*.()s-]*){9,}.*|.*@.*',
          message: INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
        })(value, key, attrs);
      }
    },
    suspicious: v.suspicious(),
    inputLength: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (attrs[key] && attrs[key].trim().length > 0 && checkEmploymentStatus(attrs, statuses, true)) {
        return v.inputLength({
          max: 40,
          message: 'Your position name must be 40 characters or less.',
        })(value, key, attrs);
      }
    },
  },
  tenureSecondary: {
    required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
        return v.required({ message: 'Enter a valid number of years' })(value, key, attrs);
      }
    },
    range: (value, key, attrs) => {
      // TODO DRY
      return tenureRange(value, key, attrs, true);
    },
    suspicious: v.suspicious(),
    isNumber: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (attrs.hasOwnProperty(key) && checkEmploymentStatus(attrs, statuses, true)) {
        return v.isNumber({ message: 'Please select a valid job tenure' })(value, key, attrs);
      }
    },
  },
  workAddressSecondary: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
          return v.required({ message: 'Your work address is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
  },
  citySecondary: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
          return v.required({ message: 'A valid city is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
    customCondition: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (checkEmploymentStatus(attrs, statuses, true)) {
        return v.customCondition({
          condition: CUSTOM_REGULAR_EXP,
          message: INVALID_SENSITIVE_CONTENT_IN_NAME_FIELD,
        })(value, key, attrs);
      }
    },
  },
  stateSecondary: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
          return v.required({ message: 'A valid state is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
  },
  zipCodeSecondary: {
    required: skipIfMissing({
      validateFn: (value, key, attrs) => {
        if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
          return v.required({ message: 'A valid zip code is required' })(value, key, attrs);
        }
      },
    }),
    suspicious: v.suspicious(),
    validZipcode: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
        return v.validZipcode()(value, key, attrs);
      }
    },
  },
  workPhoneSecondary: {
    /* required: (value, key, attrs) => {
      if (checkEmploymentStatus(attrs, ['employed', 'part_time'], true)) {
        return v.required({ message: 'A valid work phone number is required' })(value, key, attrs);
      }
    }, */ suspicious: v.suspicious(),
    validTelephone: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (checkEmploymentStatus(attrs, statuses, true)) {
        return v.validTelephone()(value, key, attrs);
      }
    },
    /* isSameAsPrimaryPhone: (value, key, attrs) => {
      const statuses = ['employed', 'contractor', 'part_time', 'selfemployed'];
      if (checkEmploymentStatus(attrs, statuses, true)) {
        const phoneNumberToCompare = attrs.primaryPhoneSecondary;
        return isSameAsPrimaryPhone()(value, key, phoneNumberToCompare);
      }
    }, */
  },
  alternatePhoneSecondary: {
    suspicious: v.suspicious(),
    validTelephone: v.validTelephone(),
    isSameAsPrimaryPhone: (value, key, attrs) => {
      if (attrs.hasOwnProperty(key)) {
        const phoneNumberToCompare = attrs.primaryPhoneSecondary;
        return isSameAsPrimaryPhone()(value, key, phoneNumberToCompare);
      }
    },
  },
  skipWorkAddress: true,
  dscQuestion: {
    required: skipIfMissing({ validateFn: v.required({ message: 'An answer is required' }) }),
  },
  sentilinkConsent: {
    required: skipIfMissing({ validateFn: isTruthy({ message: SENTILINK_AGREEMENTS.validationErrorText }) }),
  },
};

const validations = fieldName => (fieldName ? { [fieldName]: allValidations[fieldName] } : allValidations);
export default validations;
export { allValidations as schema };

